import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  getChatNotificationsApi,
  markChatAsReadApi,
  markInternalChatAsReadApi,
} from "../services/chat.services";
import { getCookie } from "../helpers/localStorage";
import {
  ACCESS_TOKEN,
  FacilityID,
  ORGANISATONID,
} from "../constants/defaultKeys";

export const getNotificationKey = (item) =>
  item?.session_id ||
  `${item?.patient_id}_${item?.chat_type}_${item?.ip_id}`;

export const useChatNotifications = (isDoctor = false) => {
  const token = getCookie(ACCESS_TOKEN);
  const organizationId = getCookie(ORGANISATONID);
  const facility = getCookie(FacilityID);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  const ws = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!facility) return;
    try {
      setLoading(true);
      const response = await getChatNotificationsApi(facility);
      setNotifications(response.data?.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching chat notifications:", err);
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [facility]);

  const dismissNotification = useCallback((item) => {
    const key = getNotificationKey(item);
    setNotifications((prev) =>
      prev.filter((entry) => getNotificationKey(entry) !== key)
    );
  }, []);

  const markNotificationAsRead = useCallback(async (item) => {
    const sessionId =
      item?.ip_id || item?.session_id?.split?.("_")?.[0] || item?.session_id;

    if (!sessionId) return;

    const markRead =
      item?.chat_type === "internal"
        ? markInternalChatAsReadApi
        : markChatAsReadApi;

    await markRead(sessionId);
  }, []);

  const handleNotificationClick = useCallback(
    (item) => {
      dismissNotification(item);
      markNotificationAsRead(item).catch((err) => {
        console.error("Failed to mark notification as read:", err);
        fetchNotifications();
      });
    },
    [dismissNotification, markNotificationAsRead, fetchNotifications]
  );

  const unreadBadgeCount = useMemo(
    () =>
      notifications.reduce((sum, item) => {
        const count = Number(item?.unread_count);
        if (Number.isFinite(count) && count > 0) return sum + count;
        return sum + 1;
      }, 0),
    [notifications]
  );

  useEffect(() => {
    fetchNotifications();

    const wsUrl = `${process.env.REACT_APP_WS_BASE_URL}/ws/notifications/?token=${token}&organization=${organizationId}&facility=${facility}`;
    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => console.log("WebSocket Connected");

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.notification) {
          setNotification(data);
          fetchNotifications();
          setTimeout(() => setNotification(null), 5000);
        }
      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };

    ws.current.onerror = (event) => {
      console.log("WebSocket error:", event);
    };

    ws.current.onclose = () => {
      console.log("WebSocket Disconnected");
    };

    return () => {
      if (ws.current) ws.current.close();
    };
  }, [fetchNotifications, token, organizationId, facility]);

  return {
    notifications,
    loading,
    error,
    notification,
    unreadBadgeCount,
    refetch: fetchNotifications,
    dismissNotification,
    handleNotificationClick,
  };
};
